import { ArrowLeftOutlined } from '@ant-design/icons';
import { Alert, Button, Col, Divider, Row } from 'antd';
import { useMessageContext } from 'components/Message/CreateMessageComponent/MessageContext';
import ContentPreview from 'components/Message/CreateMessageComponent/StepMessage/ContentPreview/ContentPreview';
import UploadArea from 'components/Message/CreateMessageComponent/StepMessage/ManualCreation/UploadArea/UploadArea';
import VideoRecorder from 'components/Message/CreateMessageComponent/StepMessage/VideoRecorder/VideoRecorder';
import { useMessageService } from 'components/Message/useMessageService';
import { usePermissionsService } from 'components/Message/usePermissionsService';
import { useEffect, useState } from 'react';
import { getSupportLink } from 'utils/utils';

type AllProps = {
  scriptText?: string;
  initTitle?: string;
  onNext: (shouldExit?: boolean) => void;
  onBack: () => void;
  goToDeliveryInfo: () => void;
};

function VideoEditor({ scriptText, initTitle, onBack, onNext, goToDeliveryInfo }: AllProps) {
  const { message } = useMessageService();
  const { permissionState, requestPermissions, checkPermissions } = usePermissionsService({
    onSuccess: () => setMode('composing')
  });

  const [mode, setMode] = useState<'init' | 'composing' | 'preview'>(scriptText ? 'composing' : 'init');
  const { file, setFile } = useMessageContext();

  useEffect(() => {
    if (file) {
      setMode('preview');
    }
  }, [file]);

  if (!message) return null;

  return (
    <div className="step-creation-video-editor create-message-component-full-height">
      {mode === 'preview' ? (
        <ContentPreview
          message={message}
          onNext={onNext}
          onBack={() => setMode(file ? 'init' : 'composing')}
          onRestart={() => {
            setMode('init');
          }}
          goToDeliveryInfo={goToDeliveryInfo}
          file={file}
          text={scriptText}
          title={initTitle}
        />
      ) : mode === 'composing' ? (
        <>
          <VideoRecorder
            scriptText={scriptText}
            onDone={(file) => {
              setFile(file);
              setMode('preview');
            }}
            onCancel={() => {
              if (scriptText) {
                onBack();
              } else {
                setMode('init');
              }
            }}
          />
        </>
      ) : (
        <>
          <div>
            <UploadArea
              mediaConstraintKey="videoMessage"
              onSelectFile={(file) => {
                setFile(file);
                setMode('preview');
              }}
            />

            <Divider plain>or</Divider>

            <div className="sub-action">
              {permissionState === 'prompt' ? (
                <div className="access-camera">
                  <Alert
                    message="Provide Access to Camera"
                    description={
                      <>
                        Click <strong>Allow</strong> in popup.
                      </>
                    }
                    type="info"
                    showIcon
                    action={
                      <Button type="primary" onClick={() => requestPermissions({ video: true, audio: true })}>
                        Allow Camera &amp; Microphone
                      </Button>
                    }
                  />
                </div>
              ) : permissionState === 'denied' ? (
                <div className="access-camera">
                  <Alert
                    message="Access to Camera is Denied!"
                    description={
                      <>
                        {getSupportLink() ? (
                          <>
                            Follow{' '}
                            <a href={getSupportLink()} target="_blank" rel="noopener noreferrer">
                              official documentation
                            </a>{' '}
                            to provide access to camera.
                          </>
                        ) : (
                          <>Please provide access to your Camera.</>
                        )}
                      </>
                    }
                    type="error"
                    showIcon
                  />
                </div>
              ) : (
                <>
                  <div className="sub-text">Record a video message directly in app!</div>
                  <Button
                    size="large"
                    type="primary"
                    onClick={() => checkPermissions(['camera', 'microphone'])}
                    className="sub-button"
                  >
                    Create Your Own Video
                  </Button>
                </>
              )}
            </div>
          </div>

          <Row className="create-message-component-footer">
            <Col span={12}>
              <Button onClick={onBack} type="link">
                <ArrowLeftOutlined /> Back
              </Button>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
}

export default VideoEditor;
